import { useState, useMemo, useEffect } from "react";

import type { StateOrName } from "@uirouter/react";
import dayjs from "dayjs";
import styled from "styled-components";

import Sentry from "@similarweb/sentry-logger";
import { FlexColumn, PixelPlaceholderLoader, PlainTooltip } from "@similarweb/ui-components";

import { keywordGroupsQueryClient, keywordGroupsTransforms, useAllKeywordGroups } from "app/services/keyword-groups";
import {
    getRecentsAnalysis,
    getRecentsByParentState,
} from "app/services/solutions2Services/HomepageDataFetchers/NewModulesHomepageDataFetcher";
import type { IRecentsResult, Recent } from "app/custom-hooks/useRecentsHook";
import { useRecents } from "app/custom-hooks/useRecentsHook";
import { translate } from "app/filters/i18nFilter";
import { KeywordListLabel } from "app/pages/digital-suite/home-page/components/RecentsWidget/KeywordListLabel/KeywordListLabel";
import { RecentsWidget } from "app/pages/digital-suite/home-page/components/RecentsWidget/RecentsWidget";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { resolveRouterTrackingId } from "single-spa/react-app/router/resolveRouterTrackingId";
import { AssetsService } from "app/services/AssetsService";
import { useWebsitesFavicons } from "app/services/sitesService/hooks/useWebsitesFavicons";
import { RecentCard } from "./RecentCard/RecentCard";
import { TooltipContent } from "./TooltipContent/TooltipContent";
import type { IRecentItem } from "./types";
import { RecentType } from "./types";
import { allTrackers } from "app/services/track/track";

const CARD_HEIGHT = 65;
const MAX_KEYWORD_NAME_CHARS = 18;

const LoaderContainer = styled(FlexColumn)`
    gap: 16px;
    width: 100%;
`;

export const getComparedWebsites = (recentObj): Array<IRecentItem> => {
    if (!recentObj) {
        return [];
    }
    const itemsIndices = Array.from(Array(recentObj.data?.comparedItems?.length ?? 0).keys());

    return itemsIndices.map((item) => ({
        name: recentObj.data?.comparedItems[item],
        icon: recentObj.data?.resources?.comparedItemsFavicon?.[item] || "",
    }));
};

export const KeywordsWebsitesRecentsWidget = ({
    filterRecentsBy,
    withoutKeywords = false,
    suggestedEntities,
    suggestionsTargetState,
}: {
    filterRecentsBy?: StateOrName | StateOrName[];
    withoutKeywords?: boolean;
    suggestedEntities?: string[];
    suggestionsTargetState?: string;
}) => {
    const [recentWebsites, setRecentWebsites] = useState<IRecentsResult["recents"]>([]);
    const { recents: keywordsRecents } = useRecents("keyword", true);
    const [isLoading, setIsLoading] = useState(true);
    const [suggestedResults, setSuggestedResults] = useState<Partial<Recent>[]>([]);
    const { userGroups, sharedGroups } = useAllKeywordGroups();

    const relevantRecents = useMemo(
        () =>
            withoutKeywords
                ? recentWebsites
                : [...recentWebsites, ...keywordsRecents]
                      .filter((recent) => recent?.data?.stateName)
                      .sort((a, b) => {
                          const aTime = dayjs(a.updatedTime).valueOf();
                          const bTime = dayjs(b.updatedTime).valueOf();
                          return bTime - aTime;
                      }),
        [recentWebsites, keywordsRecents, withoutKeywords],
    );

    const [keywordIcons, setKeywordIcons] = useState<{ keyword: string; keywordGroup: string }>({
        keyword: "",
        keywordGroup: "",
    });

    const { data: entitiesFavicons = {} } = useWebsitesFavicons(suggestedEntities || [], undefined, {
        enabled: suggestedEntities?.length > 0,
    });

    useEffect(() => {
        if (suggestedEntities && suggestionsTargetState && Object.keys(entitiesFavicons).length > 0) {
            const targetState = proNavigator.getState(suggestionsTargetState);
            const suggestionsInfo = Object.entries(entitiesFavicons).map(([key, favicon]) => {
                return {
                    id: key,
                    data: {
                        type: "website",
                        mainItem: key,
                        resources: { mainItemFavIcon: favicon },
                        pageTitle: targetState.pageTitle,
                        comparedItems: [],
                        stateName: suggestionsTargetState,
                        params: { key },
                    },
                } as unknown as Partial<Recent>;
            });

            setSuggestedResults(suggestionsInfo);
        }
    }, [suggestedEntities, suggestionsTargetState, entitiesFavicons]);

    //initialize keywords settings
    useEffect(() => {
        void (async () => {
            try {
                if (filterRecentsBy) {
                    await keywordGroupsQueryClient.invalidateAll();
                    const recentWebsites = await getRecentsByParentState(filterRecentsBy, true);
                    setRecentWebsites(recentWebsites);
                } else {
                    const recentWebsites = await getRecentsAnalysis("website", true);
                    setRecentWebsites(recentWebsites);
                }
            } catch (error) {
                Sentry.error("Error fetching recents", error as Error);
            } finally {
                setIsLoading(false);
            }
        })();

        setKeywordIcons({
            keyword: AssetsService.assetUrl(`/images/recents/keyword.svg`),
            keywordGroup: AssetsService.assetUrl(`/images/recents/keyword-group.svg`),
        });
    }, []);

    const getKeywordData = (keyword: string): IRecentItem => {
        const trimKeywordName = (keyword: string, maxChars: number): string => {
            const overflow = keyword.length > maxChars;
            return overflow ? `${keyword.slice(0, maxChars)}...` : keyword;
        };

        const isGroup = keywordGroupsTransforms.isKeywordGroup(keyword);
        const rawName = isGroup
            ? ([...userGroups, ...sharedGroups].find((group) => group.Id === keyword.slice(1))?.Name ?? "")
            : keyword;
        const trimmedName = trimKeywordName(rawName, MAX_KEYWORD_NAME_CHARS);

        if (isGroup) {
            return {
                name: `${trimmedName} (${translate("digitalmarketing.homepage.recents_widget.keyword_group.suffix")})`,
                element: <KeywordListLabel name={trimmedName} />,
                icon: keywordIcons.keywordGroup,
            };
        } else {
            return {
                name: `'${trimmedName}'`,
                icon: keywordIcons.keyword,
            };
        }
    };

    const renderRecentCard = (recent: Recent) => {
        let comparedItems: Array<IRecentItem> = [];
        let mainItem: IRecentItem = { name: "", icon: "" };

        switch (recent?.data?.type) {
            case RecentType.Website:
                mainItem = {
                    name: recent.data.mainItem,
                    icon: recent.data.resources?.mainItemFavIcon,
                };
                comparedItems = getComparedWebsites(recent);
                break;

            case RecentType.Keyword: {
                mainItem = recent.data?.keyword ? getKeywordData(recent.data.keyword) : { name: undefined };
                comparedItems = recent.data?.comparedItems?.map((keyword) => getKeywordData(keyword)) || [];

                /**
                 * Check if all keyword groups are still available for the user.
                 * A group without a name means that it's no longer available.
                 */
                const cardValid = [mainItem, ...comparedItems].every((item) => !!item?.name);
                if (!cardValid) {
                    return null;
                }

                break;
            }

            default:
                return null;
        }

        return (
            <PlainTooltip
                key={recent.id}
                enabled={recent.data.comparedItems?.length > 1}
                placement={"bottom"}
                debounce={1500}
                tooltipContent={
                    <TooltipContent
                        mainItem={mainItem}
                        comparedItems={comparedItems}
                    />
                }
            >
                <div
                    data-automation="recent-card"
                    data-automation-type={recent.data.type}
                >
                    <RecentCard
                        height={CARD_HEIGHT}
                        // Don't show last visit for suggestions
                        lastVisit={relevantRecents?.length ? dayjs(recent.updatedTime).local().toDate() : undefined}
                        durationFilter={recent.data.duration}
                        countryFilter={recent.data.country}
                        targetPageName={recent.data.stateName}
                        targetPageParams={recent.data.params}
                        mainItem={mainItem}
                        comparedItems={comparedItems}
                        onCardClick={() => {
                            const targetState = proNavigator.getState(recent.data.stateName);
                            const { section, subSection, subSubSection } =
                                resolveRouterTrackingId(targetState, recent.data.params) ?? {};
                            const page = subSubSection || subSection || section;

                            if (!page) {
                                return;
                            }

                            allTrackers.dispatchTrackEvent(
                                "Button",
                                "click",
                                `Recent Queries/Recent page click/${page || ""}/${recent.data.type || ""}`,
                            );
                        }}
                    />
                </div>
            </PlainTooltip>
        );
    };

    return isLoading ? (
        <LoaderContainer>
            <PixelPlaceholderLoader
                height={63}
                width="100%"
            />
            <PixelPlaceholderLoader
                height={63}
                width="100%"
            />
            <PixelPlaceholderLoader
                height={63}
                width="100%"
            />
        </LoaderContainer>
    ) : (
        <RecentsWidget
            renderRecentCard={renderRecentCard}
            relevantRecents={relevantRecents?.length ? relevantRecents : suggestedResults}
            cardHeight={CARD_HEIGHT}
            title={relevantRecents?.length ? undefined : "digitalmarketing.homepage.suggestions_widget.title"}
            compactDisplay={relevantRecents?.length ? 3 : 5}
        ></RecentsWidget>
    );
};
